import React, { useEffect, useState } from 'react';
import {
  Divider,
  Grid,
  Header,
  Icon,
  Loader,
  Message,
  Modal,
  Segment,
  Table
} from 'semantic-ui-react';
import styled from 'styled-components';

import Button from '../../../components/Button';
import { DateOnlyToStringDate } from '../../../services/date';
import {
  BondInsuranceInvoiceDTO,
  BondInsuranceInvoiceDetailDTO,
  BondInsuranceInvoiceFileLinkDTO,
  getBondInsuranceInvoiceAttachments,
  getBondInsuranceInvoiceDetails
} from '../../../services/bond-insurance-invoices';
import { maskCpfOrCnpj, numberToMoneyString } from '../../../services/masks';
import { dangerNotification, warningNotification } from '../../../services/notification';
import { FieldLabel } from '../../../styles';

import { statusLabelMap } from '../constants';

type Props = {
  open: boolean;
  invoice: BondInsuranceInvoiceDTO | null;
  onClose: () => void;
};

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const FieldBlockWrapper = styled.div`
  margin-bottom: 12px;
`;

const FieldValue = styled.div`
  margin-top: 4px;
`;

const AttachmentRow = styled.div`
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) auto;
  max-width: 520px;
  min-height: 36px;
`;

const AttachmentLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FieldBlock: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children
}) => (
  <FieldBlockWrapper>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue>{children}</FieldValue>
  </FieldBlockWrapper>
);

const formatMoney = (value?: number | string | null) => {
  if (value === undefined || value === null || value === '') return '-';
  return numberToMoneyString(Number(value));
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  return DateOnlyToStringDate(value);
};

const formatInstallment = (detail: BondInsuranceInvoiceDetailDTO) => {
  if (detail.installmentNumber && detail.installmentsAmount) {
    return `${detail.installmentNumber}/${detail.installmentsAmount}`;
  }

  if (detail.installmentNumber) {
    return String(detail.installmentNumber);
  }

  return '-';
};

const getAttachmentLabel = (attachment: BondInsuranceInvoiceFileLinkDTO) =>
  attachment.label || attachment.name || attachment.key || 'Arquivo';

const BondInsuranceInvoiceDetailsModal: React.FC<Props> = ({
  open,
  invoice,
  onClose
}) => {
  const [details, setDetails] = useState<BondInsuranceInvoiceDetailDTO[]>([]);
  const [attachments, setAttachments] = useState<BondInsuranceInvoiceFileLinkDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
  const invoiceId = invoice?.id || null;

  useEffect(() => {
    if (!open || !invoiceId) {
      setDetails([]);
      setAttachments([]);
      setError(null);
      setAttachmentsError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getBondInsuranceInvoiceDetails(invoiceId)
      .then(data => {
        if (!cancelled) {
          setDetails(data || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Não foi possível carregar os detalhes da fatura.');
          dangerNotification('Erro', 'Não foi possível carregar os detalhes da fatura.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId]);

  useEffect(() => {
    if (!open || !invoiceId) {
      return;
    }

    let cancelled = false;
    setAttachmentsLoading(true);
    setAttachmentsError(null);

    getBondInsuranceInvoiceAttachments(invoiceId)
      .then(data => {
        if (!cancelled) {
          setAttachments(data?.urls || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAttachmentsError('Não foi possível carregar os anexos.');
          dangerNotification('Erro', 'Não foi possível carregar os anexos.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAttachmentsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId]);

  const handleOpenAttachment = (attachment: BondInsuranceInvoiceFileLinkDTO) => {
    if (!attachment.link) {
      warningNotification('Arquivo indisponível', 'Não foi possível abrir este anexo.');
      return;
    }

    window.open(attachment.link, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="file alternate outline" />
        Detalhes da fatura
      </Modal.Header>
      <Modal.Content scrolling>
        {!invoice && <Message info>Nenhuma fatura selecionada.</Message>}

        {invoice && (
          <>
            <Grid stackable columns={4}>
              <Grid.Column>
                <FieldBlock label="Imobiliária">{invoice.estate?.name || '-'}</FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="Unidade">{invoice.estateUnit?.name || '-'}</FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="Seguradora">{invoice.insurer?.name || '-'}</FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="Status">
                  {statusLabelMap[invoice.status] || invoice.status || '-'}
                </FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="Número da fatura">
                  {invoice.invoiceNumber || invoice.billetNumber || '-'}
                </FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="Vencimento">{formatDate(invoice.dueDate)}</FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="Pagamento">{formatDate(invoice.paymentDate)}</FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="Valor total">{formatMoney(invoice.amount)}</FieldBlock>
              </Grid.Column>
            </Grid>

            <Divider />

            <Header as="h4">Anexos</Header>
            <Segment>
              {attachmentsLoading && <Loader active inline="centered" />}
              {!attachmentsLoading && attachmentsError && (
                <Message negative>{attachmentsError}</Message>
              )}
              {!attachmentsLoading && !attachmentsError && attachments.length === 0 && (
                <Message info>Nenhum anexo encontrado.</Message>
              )}
              {!attachmentsLoading && !attachmentsError && attachments.map(attachment => (
                <AttachmentRow key={attachment.id || attachment.key}>
                  <AttachmentLabel title={getAttachmentLabel(attachment)}>
                    <Icon name="file pdf outline" />
                    {getAttachmentLabel(attachment)}
                  </AttachmentLabel>
                  <Button
                    type="button"
                    icon="external alternate"
                    labelPosition="left"
                    onClick={() => handleOpenAttachment(attachment)}
                  >
                    Abrir
                  </Button>
                </AttachmentRow>
              ))}
            </Segment>

            <Header as="h4">Itens cobrados</Header>

            {loading && (
              <Segment basic style={{ minHeight: 160 }}>
                <Loader active inline="centered">
                  Carregando detalhes...
                </Loader>
              </Segment>
            )}

            {!loading && error && <Message negative>{error}</Message>}

            {!loading && !error && (
              <TableWrapper>
                <Table celled striped unstackable style={{ minWidth: 1180 }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Apólice</Table.HeaderCell>
                      <Table.HeaderCell>Contratação</Table.HeaderCell>
                      <Table.HeaderCell>Locatário</Table.HeaderCell>
                      <Table.HeaderCell>Documento</Table.HeaderCell>
                      <Table.HeaderCell>Endereço</Table.HeaderCell>
                      <Table.HeaderCell>Produto</Table.HeaderCell>
                      <Table.HeaderCell>Parcela</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Valor parcela</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Valor cobrado</Table.HeaderCell>
                      <Table.HeaderCell>Descrição</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {details.length === 0 ? (
                      <Table.Row>
                        <Table.Cell colSpan={10} textAlign="center">
                          Nenhum detalhe encontrado
                        </Table.Cell>
                      </Table.Row>
                    ) : (
                      details.map(detail => (
                        <Table.Row key={detail.id}>
                          <Table.Cell>{detail.policyNumber || detail.insurerPolicyId || '-'}</Table.Cell>
                          <Table.Cell>
                            {detail.bondInsuranceHiredDataId || detail.bondInsuranceId || '-'}
                          </Table.Cell>
                          <Table.Cell>{detail.tenantName || '-'}</Table.Cell>
                          <Table.Cell>{detail.tenantDocument ? maskCpfOrCnpj(detail.tenantDocument) : '-'}</Table.Cell>
                          <Table.Cell>{detail.address || '-'}</Table.Cell>
                          <Table.Cell>{detail.product || '-'}</Table.Cell>
                          <Table.Cell>{formatInstallment(detail)}</Table.Cell>
                          <Table.Cell textAlign="right">{formatMoney(detail.installmentValue)}</Table.Cell>
                          <Table.Cell textAlign="right">{formatMoney(detail.chargedAmount)}</Table.Cell>
                          <Table.Cell>{detail.description || '-'}</Table.Cell>
                        </Table.Row>
                      ))
                    )}
                  </Table.Body>
                </Table>
              </TableWrapper>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" icon="arrow left" labelPosition="left" onClick={onClose}>
          Voltar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default BondInsuranceInvoiceDetailsModal;
